import { Box, IconButton, styled, Typography, Button, TextField, FormLabel, TextareaAutosize } from "@mui/material";
import AndroidIcon from "icons/Technologies/Android.icon";
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import ReusableModal from '@components/Modal/ReusableModal.component';
import { toast } from "react-toastify";

interface TechStackCardProps {
    id: string;
    title: string;
    description: string;
    lastUpdated: string;
    onClick?: () => void;
}

const StyledCard = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: '20px',
    cursor: 'pointer',
    border: '1px solid var(--Light-Gray)',
    borderRadius: '12px',
    boxShadow: '0px 1px 2px 0px #0205111A'
})

const CustomButtonStyle = styled(IconButton)({
    border: '1px solid var(--Interviewer-Near-White)',
    borderRadius: '8px',
    width: '40px',
    height: '40px'
})

const StyledTextArea = styled(TextareaAutosize)({
    backgroundColor: '#F9F9F9',
    width: '100%',
    minHeight: '135px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Interviewer-Lightest-Black)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
    outline: 'none',
    },
})

const TechStackCard = ({id, title, description, lastUpdated, onClick}: TechStackCardProps) => {
    const [edit, setEdit] = useState(false);
    const [name, setName] = useState(title);
    const [desc, setDesc] = useState(description);
    const queryClient = useQueryClient();

    const updateTechnology = async ({ technologyId, name, description }: { technologyId: string, name: string, description: string }) => {
        const token = localStorage.getItem('token');
        const response = await axios.put(
            `https://interviewer.runasp.net/api/technologies/${technologyId}`,
            { name, description },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    };

    const mutation = useMutation({
        mutationFn: updateTechnology,
        onSuccess: () => {
            setEdit(false);
            queryClient.invalidateQueries({ queryKey: ['technologies'] });
        },
        onError: (error) => {
            console.error('Update failed:', error);
        },
    });

    const handleEditOpen = () => {
        setName(title);
        setDesc(description);
        setEdit(true);
    };

    const handleEditClose = () => {
        setEdit(false);
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't trigger onClick if modal is open or if click was on a button
        if (edit || (e.target as HTMLElement).closest('button')) {
            return;
        }
        onClick?.();
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleEditOpen();
    };

    const handleDeleteClick = (id: string) => {
        deleteMutation.mutate(id);
    }

    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const token = localStorage.getItem('token');
            const results = await axios.delete(`https://interviewer.runasp.net/api/technologies/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            return results;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['admin-technologies'] });
            toast.success('Successfully deleted technology!')
        },
        onError: (error) => {
            console.log(error);
        }
    })

    return (
        <StyledCard onClick={handleCardClick}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                <AndroidIcon />
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                    <Typography variant="h2" fontWeight="bold">{title}</Typography>
                    <Typography variant="body2">Last Updated {lastUpdated}</Typography>
                </Box>
            </Box>
            <Box sx={{display: 'flex', gap: '16px'}} onClick={(e) => e.stopPropagation()}>
                <CustomButtonStyle onClick={handleEditClick}>
                    <ModeEditOutlineOutlinedIcon fontSize="small" color="primary" sx={{width: '18px', height: '18px'}} />
                </CustomButtonStyle>
                <CustomButtonStyle onClick={() => handleDeleteClick(id)}>
                    <DeleteOutlineOutlinedIcon fontSize="small" color="primary" sx={{width: '18px', height: '18px'}} />
                </CustomButtonStyle>
            </Box>
            <ReusableModal open={edit} onClose={handleEditClose}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px', mt: '20px'}}>
                        <TextField 
                            label="Technology Name"
                            variant="outlined"
                            fullWidth
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                        <Box>
                            <FormLabel sx={{fontSize: '16px', fontWeight: '500', color: 'var(--Interviewer-Black)', mb: '2px'}}>Short Technology Description</FormLabel> 
                            <StyledTextArea 
                                value={desc}
                                onChange={e => setDesc(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <Button variant='contained' sx={{display: 'flex'}} onClick={() => {
                        mutation.mutate({
                            technologyId: id,
                            name,
                            description: desc,
                        });
                    }}>
                        Update
                    </Button>
                </Box>
            </ReusableModal>
        </StyledCard>
    );
}

export default TechStackCard;